import * as GameTypes from 'types/game.types';
import { ref, reactive, shallowRef, UnwrapRef } from 'vue';
import { defineStore } from 'pinia';
import { EventEmitter, Listener } from 'util/event-emitter';
import AchievementBanner from 'components/achievement-banner.vue';
import { default as GameScene } from 'src/scenes/game';
import { getDefaultStats, getRandomStats } from 'util/game-util';
import { AchievementDefinition } from 'src/achievements';
import { useCountdownClock } from 'src/util/composables';
import * as NamedColors from '../game/named-colors';
import { clamp, sample } from 'lodash';
import { useGlobalStore } from './global';
import { delay } from 'util/util';
import { prepareInterstitial } from 'src/util/ad-util';
import { useStatsStore } from './stats';
import { useThemeStore } from './theme';
import chroma from 'chroma-js';

type DifficultLevel = 0 | 1 | 2 | 3 | 4 | 5;
interface MatchState {
    gameOver: boolean,
    timedOut: boolean,
    completedAchievements: AchievementDefinition[],
    round: number,
    score: number,
    difficulty: DifficultLevel,
    skips: number,
    hintCount: number,
    hasteCount: number,
    precisionRounds: 0,
    precisionCount: 0,
    lastMatchSkipped: boolean,
    postRound: {
        score: number,
        multipliers: GameTypes.ResolvedScoreMultiplier[],
    }
    colorsNamed: {
        colors0: Set<GameTypes.NamedColor>,
        colors1: Set<GameTypes.NamedColor>,
        colors2: Set<GameTypes.NamedColor>
    }
}

const getMatchState = () : MatchState => {
    return {
        gameOver: false,
        timedOut: false,
        completedAchievements: [],
        round: 1,
        score: 0,
        skips: 3,
        hintCount: 0,
        hasteCount: 0,
        precisionRounds: 0,
        precisionCount: 0,
        lastMatchSkipped: false,
        postRound: {
            score: 0,
            multipliers: [],
        },
        difficulty: 4,
        colorsNamed: {
            colors0: new Set<GameTypes.NamedColor>,
            colors1: new Set<GameTypes.NamedColor>,
            colors2: new Set<GameTypes.NamedColor>
        },
    }
}

// type ScoreMultipliers = 'round' | 'haste' | 'accuracy' | 'difficulty'
// type SpecialScore

// const ScoreMultiplierMeta : { [key in DefaultScoreMultipliers]: { label: string, icon?: string } } = {
//     haste: {
//         label: 'Haste Multiplier'
//     },
//     round: {
//         label: 'Round Multiplier'
//     }
// }

interface RequestStatuses {
    statsData: GameTypes.RankedPersistedStats | false,
    interstitial: boolean
}

const useGameStore = defineStore('game-store', () => {
    const global = useGlobalStore();
    const theme = useThemeStore();
    const stats = useStatsStore();

    const events = new EventEmitter();
    const gameScene = shallowRef<GameScene>();
    const paused = ref<boolean>(false);
    const postGameReady = ref(false);
    const bannerRef = shallowRef<InstanceType<typeof AchievementBanner>>(); 
    // stats = ref<GameTypes.ServerStats>(getDefaultStats());
    const matchState = ref<MatchState>(getMatchState());
    const clock = useCountdownClock();
    const useReducedPadding = ref(false);

    /**
     * Keeps track of post-game request data. Any items set to `false` imply that the request failed for whatever reason.
     */
    const requests = reactive<RequestStatuses>({
        statsData: false,
        interstitial: false
    })

    const on = (event: string, listener: Listener) => events.on(event, listener);
    const removeListener = (event: string, listener: Listener) => events.removeListener(event, listener);
    const restage = (game: GameScene) => {
        paused.value = false;
        postGameReady.value = false;
        gameScene.value = game;
        clock.stop();
        clock.populateScene(gameScene.value);
        //stats.value = getDefaultStats();
        matchState.value = getMatchState();
        requests.interstitial = false;
        requests.statsData = false;
    }

    const hydrateBannerRef = (ref: InstanceType<typeof AchievementBanner>) => bannerRef.value = ref;
    const showBanner = async (name: string, credits: number) => bannerRef.value?.enter(name, credits);
    //const incrementScore = (count: number) => stats.value.score += count;

    const pause = () => {
        if (!matchState.value.gameOver){
            paused.value = true;
            gameScene.value?.scene.pause();
        }
    }

    const unpause = () => {
        if (!matchState.value.gameOver){
            paused.value = false;
            gameScene.value?.scene.resume();
        }
    }
    
    const end = (timedOut: boolean = false) => {
        unpause();
        matchState.value.gameOver = true;
        matchState.value.timedOut = timedOut;
    }

    const startBackgroundFx = (tint?: number) => gameScene.value?.startBackgroundFx(tint);

    const togglePaused = () => {
        paused.value ? unpause() : pause();
    }
    
    const restartGame = () => {
        unpause();
        global.startScene('RestartGame');
    }
    
    const backToMenu = () => {
        gameScene.value?.scene.resume();
        global.startScene('Menu');
    }

    // const defaultBgColor = Phaser.Display.Color.HexStringToColor('#1E2430');
    const defaultBgColor = chroma(theme.theme.gameBg);
    const currentBgColor = ref<chroma.Color>(defaultBgColor);
    const resetBackground = () => {
        let color = currentBgColor.value;
        if (color.hex() === defaultBgColor.hex()) { //do instant fadeIn if already default, prevents random flash of white
            gameScene.value?.cameras.main.fadeIn(0, ...color.rgb());
        } else if (color){ 
            gameScene.value?.cameras.main.fadeIn(500, ...color.rgb());
            currentBgColor.value = defaultBgColor;
        }
    }

    const setBackgroundColor = (color: chroma.Color) => {
        gameScene.value?.cameras.main.fadeOut(500, ...color.rgb());
        currentBgColor.value = color;
    }

    setBackgroundColor(defaultBgColor);

    const getUsedColorsForDifficulty = (level: DifficultLevel) => {
        let clamped = <0 | 1 | 2> clamp(level, 0, 2);
        return matchState.value.colorsNamed[`colors${clamped}`];
    }

    const getNamedColorsForDifficulty = (level: DifficultLevel) => {
        let clamped = <0 | 1 | 2> clamp(level, 0, 2);
        return NamedColors[`NamedColors${clamped}`];
    }

    const areAllNamedColorsUsed = () => {
        let used = getUsedColorsForDifficulty(matchState.value.difficulty);
        return used.size === getNamedColorsForDifficulty(matchState.value.difficulty).length;
    }

    const isNamedColorUsed = (color: GameTypes.NamedColor) => {
        return  getUsedColorsForDifficulty(matchState.value.difficulty).has(color);
    }

    const getNamedColor = (markAsUsed: boolean = false) : GameTypes.NamedColor => {
        if (areAllNamedColorsUsed()){
            throw new Error('ColorByName: All named colors for this difficulty level have been used.');
        }
    
        let colors = getNamedColorsForDifficulty(matchState.value.difficulty);
        const namedColor = sample(colors)!;
        if (isNamedColorUsed(namedColor)){
            return getNamedColor();
        }

        if (markAsUsed){
            getUsedColorsForDifficulty(matchState.value.difficulty).add(namedColor);
        }

        return namedColor;
    }

    const useSkip = () => {
        if (matchState.value.skips > 0){
            matchState.value.skips -= 1;
            matchState.value.lastMatchSkipped = true;
        }
    }

    const scoreRound = (baseScore: number = 25, multipliers: GameTypes.ScoreMultiplier[] = []) => {
        if (matchState.value.lastMatchSkipped){
            matchState.value.postRound.score = 0;
            matchState.value.postRound.multipliers = [];
            matchState.value.lastMatchSkipped = false;
            return;
        }

        let postRound = matchState.value.postRound;
            postRound.multipliers = [
                { label: 'Base Score', factor: 1, score: baseScore },
                { label: 'Stage Bonus', factor: matchState.value.round, score: baseScore * matchState.value.round },
                { label: 'Difficulty Bonus', factor: matchState.value.difficulty + 1, score: baseScore * (matchState.value.difficulty + 1) }
            ];

        let hasteFactor = clock.progress.value >= .85 ? 4 : 
                          clock.progress.value >= .7 ? 3 : 
                          clock.progress.value >= .5 ? 1 : 0;
        
        if (hasteFactor !== 0){
            postRound.multipliers.push({ label: 'Haste Bonus', factor: hasteFactor, score: baseScore * hasteFactor });
            matchState.value.hasteCount += 1;
        }

        matchState.value.postRound.multipliers.push(...multipliers.map(m => ({
            ...m,
            score: baseScore * m.factor
        })))

        matchState.value.postRound.score = matchState.value.postRound.multipliers.reduce((acc, curr) => acc + curr.score, 0);
        matchState.value.score += matchState.value.postRound.score;
    }

    /**
     * ------------------------------------------------------------------------------------------------
     * Post-Game Async Work
     * ------------------------------------------------------------------------------------------------
     */
    const postGameAsyncTimeout = 7000;
    const rankCurrentMatchStats = async () => {
        try {
            requests.statsData = await Promise.race<ReturnType<typeof stats.rankStats> | false>([
                stats.rankStats({
                    score: matchState.value.score,
                    rounds: matchState.value.round
                }), 
                delay<false>(postGameAsyncTimeout, false)
            ]);
        } catch (errors){
            requests.statsData = false;
        }
    }

    const prepInterstitial = async () => {
        try {
            if (global.isFirstRun){
                console.log("Interstitial Ad not shown since this is the first time the app has been run.");
                requests.interstitial = false;
                global.setIsFirstRun(false);
                return;
            }

            requests.interstitial = await Promise.race<boolean>([
                prepareInterstitial(), 
                delay<false>(postGameAsyncTimeout, false)
            ]);
        } catch (errors){
            requests.statsData = false;
        }
    }

    const preparePostGame = async () => {
        /**
         * Skip requests if the player failed on first round or didn't complete any rounds w/ score.
         */
        if (matchState.value.score !== 0){
            await Promise.all([rankCurrentMatchStats(), prepInterstitial()])
        }
        postGameReady.value = true;
    }

    return {
        on,
        removeListener,
        gameScene,
        hydrateBannerRef,
        showBanner,
        restage,
        stats,
        matchState,
        useReducedPadding,
        //incrementScore,
        paused,
        pause,
        unpause,
        togglePaused,
        restartGame,
        backToMenu,
        end,
        clock,
        currentBgColor,
        resetBackground,
        setBackgroundColor,
        areAllNamedColorsUsed,
        isNamedColorUsed,
        getNamedColor,
        startBackgroundFx,
        preparePostGame,
        postGameReady,
        scoreRound,
        requests,
        useSkip
    }
})



export { useGameStore };